import { useEffect } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

const COMPANY = "ProductSecurityReady";
const SUPPORT = "support@productsecurityready.com";
const UPDATED = "June 2026";

const LegalShell = ({ title, testId, children }) => {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="blueprint border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-5 py-14 lg:px-8 lg:py-16">
          <p className="overline text-navy-800">Legal</p>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight text-navy-900 sm:text-5xl">
            {title}
          </h1>
          <p className="mt-5 font-mono text-[11px] uppercase tracking-wide text-slate-500">
            Last updated {UPDATED}
          </p>
        </div>
      </section>
      <article
        className="mx-auto max-w-3xl space-y-8 px-5 py-14 text-sm leading-relaxed text-slate-700 lg:px-8"
        data-testid={testId}
      >
        {children}
        <p className="border-t border-slate-200 pt-8 text-xs text-slate-500">
          Questions about this document? Write to{" "}
          <a href={`mailto:${SUPPORT}`} className="font-semibold text-navy-800 underline">
            {SUPPORT}
          </a>
          .
        </p>
      </article>
      <SiteFooter />
    </div>
  );
};

const Block = ({ n, title, children }) => (
  <section className="space-y-3">
    <h2 className="font-display text-lg font-bold text-navy-900">
      <span className="mr-2 font-mono text-sm text-slate-400">{n}</span>
      {title}
    </h2>
    {children}
  </section>
);

export function Terms() {
  return (
    <LegalShell title="Terms of service" testId="terms-page">
      <p>
        These terms govern your use of the {COMPANY} website and application. By creating an account
        or using the service you agree to them. If you are accepting on behalf of an organisation,
        you confirm you are authorised to do so.
      </p>

      <Block n="01" title="What the service does">
        <p>
          {COMPANY} provides a guided, self-assessment questionnaire and produces indicative
          readiness scores and a gap report for frameworks including the EU Cyber Resilience Act,
          IEC 62443-4-1, IEC 62443-4-2, the EU Machinery Regulation 2023/1230, the UKCA conformity
          route and UK PSTI.
        </p>
        <p className="border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
          Outputs are indicative only. They are not certification, an audit, legal advice, or a
          statement of compliance or conformity with any regulation or standard. No regulation or
          standard text is reproduced. Scores derive entirely from the answers you provide, and
          responsibility for the accuracy of those answers remains with you.
        </p>
      </Block>

      <Block n="02" title="Your account">
        <p>
          You are responsible for the credentials used to access your account and for all activity
          under it. Keep your details accurate, do not share logins, and tell us promptly if you
          suspect unauthorised access. We may suspend an account that is used unlawfully, that abuses
          the service, or that attempts to disrupt or reverse engineer it.
        </p>
      </Block>

      <Block n="03" title="Subscription, plan limits and billing">
        <p>
          Assessments, indicative scores and the full gap report are currently{" "}
          <strong>free while the service is in early access</strong>, and no payment is taken. The
          paid terms below describe the Founding Plan subscription and apply only once charging is
          switched on, which we will tell you about before it happens.
        </p>
        <p>
          Generating the full gap report requires an active Founding Plan subscription at $19 per
          month. The plan covers one product with
          unlimited reassessment and report regeneration for that product. If you need reports for
          additional products, contact us and we will arrange it.
        </p>
        <p>
          Payments are processed by Paddle.com Market Limited, which acts as the merchant of record
          and reseller for the subscription. Paddle handles checkout, invoicing, applicable sales tax
          and payment disputes. Their terms apply to the payment transaction itself. Subscriptions
          renew monthly until cancelled and you can cancel at any time.
        </p>
        <p>
          The expert review service is a separate one-time engagement priced at $200 per assessment
          and is arranged by email. Scope is confirmed in writing before any work starts.
        </p>
      </Block>

      <Block n="04" title="Your content">
        <p>
          You keep all rights in the product information and answers you enter. You grant us the
          limited right to process that data to operate the service, compute your results and produce
          your report. We do not sell your data and we do not publish your product information.
        </p>
      </Block>

      <Block n="05" title="Our content">
        <p>
          The question set, scoring logic, report structure, wording and branding are our
          intellectual property. You may use and share the reports you generate inside your own
          organisation and with your customers or auditors. You may not resell the service, reproduce
          the question bank, or use it to build a competing assessment product.
        </p>
      </Block>

      <Block n="06" title="Availability">
        <p>
          We aim to keep the service available and accurate, but it is provided as is, without
          warranties of any kind. Planned maintenance, third-party outages and changes to framework
          content may interrupt access. Question content is versioned, and reports you have already
          generated remain a snapshot of the content version used at the time.
        </p>
      </Block>

      <Block n="07" title="Limitation of liability">
        <p>
          To the maximum extent permitted by law, we are not liable for indirect or consequential
          loss, lost profits, lost data, regulatory penalties, or decisions taken on the basis of an
          indicative readiness output. Our total liability for any claim is limited to the amount you
          paid us in the twelve months before the claim arose.
        </p>
      </Block>

      <Block n="08" title="Termination">
        <p>
          You may stop using the service and cancel your subscription at any time. You can delete a
          product, which removes its profile, assessment and answers. We may suspend or terminate
          access for breach of these terms. Provisions on intellectual property, liability and
          governing law survive termination.
        </p>
      </Block>

      <Block n="09" title="Changes and governing law">
        <p>
          We may update these terms as the service evolves. Material changes will be reflected in the
          date above, and continued use after an update means you accept the revised terms. These
          terms are governed by the laws applicable at our place of business, and disputes are
          subject to the courts of that jurisdiction.
        </p>
      </Block>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy notice" testId="privacy-page">
      <p>
        This notice explains what personal data {COMPANY} collects, why, and what rights you have. We
        collect the minimum needed to run the service.
      </p>

      <Block n="01" title="What we collect">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Account data</strong>: name, work email, company name, and either a hashed
            password or, if you sign in with Google, the profile email and name returned by that
            sign-in.
          </li>
          <li>
            <strong>Assessment data</strong>: the product profile and answers you enter, plus the
            scores, findings and reports generated from them.
          </li>
          <li>
            <strong>Billing data</strong>: subscription status and payment references. Card details
            are handled by Paddle and never reach our servers.
          </li>
          <li>
            <strong>Technical and usage data</strong>: IP address for rate limiting and abuse
            prevention, login attempts, and product analytics events such as "assessment completed"
            used to understand where the service can be improved.
          </li>
          <li>
            <strong>Enquiries</strong>: anything you send us in a consulting enquiry, expert review
            request or support email.
          </li>
        </ul>
      </Block>

      <Block n="02" title="Why we use it">
        <p>
          To create and secure your account, to compute your readiness results and produce reports,
          to take payment and provide support, to send transactional email such as password resets
          and receipts, and to protect the service against abuse. We do not sell personal data and we
          do not use your product information to train models for other customers.
        </p>
      </Block>

      <Block n="03" title="Legal bases">
        <p>
          Where the GDPR or UK GDPR applies, we rely on contract performance for providing the
          service, legitimate interests for security, abuse prevention and service improvement, legal
          obligation for accounting records, and consent where you have explicitly given it.
        </p>
      </Block>

      <Block n="04" title="Processors we use">
        <ul className="list-disc space-y-2 pl-5">
          <li>Paddle, for payment processing as merchant of record.</li>
          <li>An email delivery provider, for transactional email only.</li>
          <li>Cloud hosting and database infrastructure, for running the application.</li>
          <li>Google, only if you choose to sign in with a Google account.</li>
        </ul>
        <p>
          These providers act on our instructions under contract. Some may process data outside your
          country, under recognised transfer safeguards.
        </p>
      </Block>

      <Block n="05" title="Retention">
        <p>
          Account and assessment data is kept while your account is active. Deleting a product
          removes its profile, assessment and answers. Billing and payment records are kept for the
          period required by accounting and tax rules. Ask us to close your account and we will
          delete personal data that we are not legally required to retain.
        </p>
      </Block>

      <Block n="06" title="Security">
        <p>
          Passwords are stored only as salted hashes. Access is authenticated per request, accounts
          are protected against brute-force attempts, and reports are only reachable by the account
          that owns them. Administrators can access account data where required to provide support,
          and administrative access to a customer report is recorded in an audit log.
        </p>
      </Block>

      <Block n="07" title="Cookies">
        <p>
          We use strictly necessary cookies and browser storage to keep you signed in and to protect
          the session. We do not run advertising cookies or cross-site ad tracking.
        </p>
      </Block>

      <Block n="08" title="Your rights">
        <p>
          Subject to local law you can request access, correction, deletion, a copy of your data, or
          object to certain processing. Write to {SUPPORT} and we will respond within the period
          required by the applicable law. You may also complain to your local data protection
          authority.
        </p>
      </Block>
    </LegalShell>
  );
}

export function RefundPolicy() {
  return (
    <LegalShell title="Refund policy" testId="refund-page">
      <p>
        We want the service to be worth paying for. This policy explains when we refund and how to
        ask.
      </p>
      <p className="border border-teal-200 bg-teal-50 p-4 text-xs text-teal-900">
        The service is currently free while in early access and no charges are taken, so nothing is
        payable and nothing needs refunding today. This policy applies once paid subscriptions are
        switched on.
      </p>

      <Block n="01" title="Assess before you pay">
        <p>
          The assessment, indicative scores per framework, risk band, evidence confidence and a
          preview of your top gaps are all free. You only subscribe once you have seen your own
          results, so you know what you are buying before any payment is taken.
        </p>
      </Block>

      <Block n="02" title="Fourteen day refund on your first subscription payment">
        <p>
          If the full report does not give you what you expected, ask us within fourteen days of your
          first subscription payment and we will refund it in full and cancel the subscription. You
          do not need to give a reason. If you are a consumer in the EU or UK, this sits alongside,
          and does not reduce, your statutory withdrawal rights.
        </p>
      </Block>

      <Block n="03" title="Renewals and cancellation">
        <p>
          The Founding Plan renews monthly. You can cancel at any time and you keep access until the
          end of the period already paid for. We do not automatically refund a renewal that has
          already been charged, but if a renewal took you by surprise, contact us within seven days
          and we will look at it fairly.
        </p>
      </Block>

      <Block n="04" title="Service problems">
        <p>
          If a technical fault on our side prevented you from generating or downloading your report,
          tell us and we will either fix it promptly or refund the affected period, whichever you
          prefer.
        </p>
      </Block>

      <Block n="05" title="Expert review">
        <p>
          The one-time expert review is arranged by email and scoped in writing before work begins.
          It is refundable in full until the reviewer starts work. Once the review has started it is
          refundable pro rata for the work not yet performed.
        </p>
      </Block>

      <Block n="06" title="What is not refunded">
        <p>
          We do not refund on the basis of the score itself. An indicative readiness result is the
          product of your own answers, so a low score is an output of the service, not a defect in
          it. We may decline repeated refund requests from the same account where the service was
          used and reports were generated.
        </p>
      </Block>

      <Block n="07" title="How to request a refund">
        <p>
          Email {SUPPORT} from the address on your account with your order or subscription reference.
          Payments are processed by Paddle as merchant of record, so approved refunds are returned by
          Paddle to the original payment method, typically within five to ten business days depending
          on your bank.
        </p>
      </Block>
    </LegalShell>
  );
}
